import React from "react";
import { useImmer } from "use-immer";

import { IgDialog } from "@intelligems/ig-theme/IgDialog";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useIsHeadless } from "@src/api/configurations/is-headless";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { useGetScriptStatus } from "@src/api/shops/get-script-status";
import { ConnectToShopifyContent } from "@src/components/ConnectToShopify/ConnectToShopifyContent";
import { useDialog } from "@src/hooks/dialog";
import { mpScriptIntegration } from "@src/utils/mixpanel";

export const ConnectToShopifyDialog = () => {
  const theme = useTheme();
  const org = useGetOrg();
  const dialog = useDialog("connectToShopify");
  const isHeadless = useIsHeadless();
  const scriptStatusQuery = useGetScriptStatus(org.data?.org.id);
  const [verified, setVerified] = useImmer(false);

  const shouldShowDialog =
    !isHeadless &&
    dialog.isOpen &&
    (verified ||
      scriptStatusQuery?.data?.type === "missing" ||
      (verified && !!scriptStatusQuery?.data?.exists));

  const successContent = (
    <Grid
      container
      direction="column"
      rowSpacing={1}
      textAlign="center"
      alignItems="center"
    >
      <Grid sx={{ width: "150px", height: "150px" }}>
        <DotLottieReact
          src="/lottie/script_status_check_complete.lottie"
          loop={true}
          autoplay={true}
          renderConfig={{ autoResize: true }}
        />
      </Grid>
      <Grid>
        <IgTypography variant="h1">You're ready to go!</IgTypography>
      </Grid>
      <Grid sx={{ width: "400px" }}>
        <IgTypography color={theme.palette.text.secondary}>
          We've confirmed that the script is on your store theme and working as
          intended. You can close this window and start using Intelligems.
        </IgTypography>
      </Grid>
    </Grid>
  );

  return (
    <IgDialog
      padding="24px"
      showDialog={shouldShowDialog}
      loading={scriptStatusQuery.isFetching}
      showCloseIcon={verified}
      handleClose={() => {
        if (verified) {
          mpScriptIntegration(
            "Connect App embed page",
            "Close script install success modal"
          );
        }
        dialog.close();
      }}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(8px)",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
          },
        },
        paper: {
          sx: {
            width: verified ? "500px" : "672px",
            maxWidth: "672px",
          },
        },
      }}
      title=" "
      hideActions={true}
      shouldNotClose={true}
      dontUseDialogContent={true}
      rawContent={
        <ConnectToShopifyContent
          onContinue={() => dialog.close()}
          onSkip={() => dialog.close()}
          successContent={successContent}
          screenName="Connect App embed page"
          onScriptVerified={() => setVerified(true)}
        />
      }
    />
  );
};
